import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Rocket, Plus, Trash2 } from "lucide-react";

const CATEGORIES = [
  { value: "new", label: "Nouveauté" },
  { value: "improvement", label: "Amélioration" },
  { value: "fix", label: "Correction" },
  { value: "security", label: "Sécurité" },
  { value: "design", label: "Design" },
  { value: "automation", label: "Automatisation" },
  { value: "integration", label: "Intégration" },
];

export default function VersionsAdminPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState("…");
  const [type, setType] = useState("patch");
  const [titleFr, setTitleFr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [showPopup, setShowPopup] = useState(true);
  const [mandatory, setMandatory] = useState(false);
  const [items, setItems] = useState([{ category: "new", title_fr: "", title_en: "" }]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/releases/current").then(({ data }) => setCurrent(data.version)).catch(() => {});
    api.get("/releases/history").then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(load, []);

  if (user && !user.is_staff) return <Navigate to="/app" replace />;

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { category: "new", title_fr: "", title_en: "" }]);
  const removeItem = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  const publish = async () => {
    if (!titleFr.trim()) { toast.error("Titre (FR) requis"); return; }
    setSaving(true);
    try {
      const payload = {
        release_type: type, title_fr: titleFr, title_en: titleEn || undefined,
        show_popup: showPopup, mandatory_update: mandatory,
        items: items.filter((it) => it.title_fr.trim()),
      };
      const { data } = await api.post("/releases", payload);
      toast.success(`Version ${data.version} publiée`);
      setTitleFr(""); setTitleEn(""); setItems([{ category: "new", title_fr: "", title_en: "" }]);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur de publication");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#EAB308] focus:outline-none";

  return (
    <div className="space-y-8" data-testid="versions-admin-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10">
          <Rocket className="w-5 h-5 text-[#EAB308]" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-white">Versions & mises à jour</h1>
          <p className="text-sm text-white/50">PROGÈRE CORE — version actuelle <span className="font-mono text-[#EAB308]">v{current}</span></p>
        </div>
      </div>

      {/* Formulaire de publication */}
      <div className="rounded-xl border border-white/10 bg-[#111217] p-6 space-y-4">
        <h2 className="text-lg font-bold text-white">Publier une nouvelle version</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-white/50">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="release-type">
              <option value="patch">Correctif (patch)</option>
              <option value="minor">Fonctionnalité (minor)</option>
              <option value="major">Majeure (major)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70 mt-6"><input type="checkbox" checked={showPopup} onChange={(e) => setShowPopup(e.target.checked)} className="accent-[#EAB308]" /> Afficher le popup</label>
          <label className="flex items-center gap-2 text-sm text-white/70 mt-6"><input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} className="accent-[#EAB308]" /> Mise à jour obligatoire</label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-white/50">Titre (FR)</label><input value={titleFr} onChange={(e) => setTitleFr(e.target.value)} className={inputCls} data-testid="release-title-fr" placeholder="Ex: Améliorations & correctifs" /></div>
          <div><label className="text-xs text-white/50">Titre (EN)</label><input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} className={inputCls} placeholder="Optionnel" /></div>
        </div>

        <div className="space-y-2">
          <label className="text-xs text-white/50">Éléments</label>
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <select value={it.category} onChange={(e) => setItem(i, "category", e.target.value)} className={inputCls + " max-w-[160px]"}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <input value={it.title_fr} onChange={(e) => setItem(i, "title_fr", e.target.value)} className={inputCls} placeholder="Description (FR)" data-testid={`release-item-${i}`} />
              <button onClick={() => removeItem(i)} className="shrink-0 rounded-lg border border-white/10 px-3 text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addItem} className="inline-flex items-center gap-1.5 text-sm text-[#EAB308] hover:underline"><Plus className="w-4 h-4" /> Ajouter un élément</button>
        </div>

        <button onClick={publish} disabled={saving} data-testid="release-publish" className="rounded-lg bg-[#EAB308] px-6 py-2.5 text-sm font-bold text-black hover:bg-[#ffd45c] disabled:opacity-50 transition-colors">
          {saving ? "Publication…" : "Publier la version"}
        </button>
      </div>

      {/* Historique */}
      <div className="rounded-xl border border-white/10 bg-[#111217] p-6">
        <h2 className="text-lg font-bold text-white mb-4">Historique</h2>
        <div className="space-y-3">
          {history.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-lg border border-white/8 bg-black/30 px-4 py-3" data-testid={`history-${r.version}`}>
              <div className="flex items-center gap-3">
                <span className="rounded px-2 py-0.5 text-[11px] font-black text-black bg-[#EAB308]">v{r.version}</span>
                <span className="text-sm text-white/80">{r.title_fr}</span>
              </div>
              <span className="text-xs text-white/40">{(r.items || []).length} élément(s) · {r.release_type}</span>
            </div>
          ))}
          {history.length === 0 && <p className="text-white/40 text-sm">Aucune version.</p>}
        </div>
      </div>
    </div>
  );
}
